import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LANGUAGE_STORAGE_KEY = 'appLanguage';

const storedLanguage = (() => {
    try {
        return localStorage.getItem(LANGUAGE_STORAGE_KEY);
    } catch {
        return null;
    }
})();

i18n.use(initReactI18next).init({
    lng: storedLanguage === 'sk' ? 'sk' : 'en',
    fallbackLng: 'en',
    interpolation: {
        escapeValue: false
    },
    resources: {
        en: {
            translation: {
                login: {
                    title: 'Sign In',
                    email: 'Email Address',
                    password: 'Password',
                    submit: 'Sign In',
                    success: 'Successfully signed in',
                    error: 'Failed to sign in',
                    deviceLogin: 'Sign in on another device',
                    deviceLoginHint: 'Show a short code on a TV or another PC — no password typing.',
                    approveDevice: 'Approve a device code',
                    token: {
                        label: 'Access Token',
                        placeholder: 'Paste your API token here',
                        submit: 'Sign In with Token',
                        invalid: 'Token is invalid or expired',
                    },
                    validation: {
                        email: {
                            required: 'Email is required',
                            invalid: 'Invalid email format'
                        },
                        password: {
                            required: 'Password is required'
                        }
                    }
                },
                deviceLogin: {
                    title: 'Device sign-in',
                    subtitle: 'Scan the QR code or enter this code on a signed-in phone or computer.',
                    codeLabel: 'Your code',
                    instructions: 'On your other device open {{url}} and enter the code (or scan the QR).',
                    waiting: 'Waiting for approval…',
                    expiresIn: 'Expires in {{time}}',
                    expired: 'This code expired. Generate a new one to continue.',
                    retry: 'Generate new code',
                    error: 'Could not start device sign-in. Try again.',
                    success: 'Device signed in successfully',
                    qrAlt: 'QR code to approve this device',
                    backToLogin: 'Back to password sign-in',
                },
                deviceLink: {
                    title: 'Approve device',
                    subtitle: 'Enter the code shown on your TV or other device to sign it in.',
                    codeLabel: 'Device code',
                    submit: 'Approve and sign in',
                    success: 'Device approved',
                    successDetail: 'The other device is now signed in. You can close this page.',
                    error: 'Could not approve this code. Check it and try again.',
                },
                dashboard: {
                    title: 'Dashboard',
                    welcome: 'Welcome to your dashboard',
                    logout: 'Logout',
                    logoutSuccess: 'Successfully logged out',
                    globalSearchPlaceholder:
                        'Search everywhere — notes, tasks, projects, paths, external assets, accounting',
                    globalSearchHelper:
                        'Uses the same server search as Notes/Tasks lists (includes raw HTML in descriptions). Projects, Path Opener, External Assets, and Accounting filter locally in the loaded catalog.',
                    clearSearch: 'Clear search',
                    clearSearchButton: 'Clear',
                    globalSearchSectionLabel: 'Global search',
                    globalSearchRightClickPaste: 'Right-click paste',
                    globalSearchRightClickPasteHint:
                        'When on, right-click in the search field pastes the first line from the clipboard and opens the browser menu only if paste fails.',
                    tabModules: 'Modules',
                    tabRecentActivity: 'Recent activity',
                    recentActivityHelper:
                        '{{count}} items from data already loaded on this page, newest updates first.',
                    recentActivityEmpty: 'No matching activity.',
                    activityType: {
                        note: 'Note',
                        task: 'Task',
                        project: 'Project',
                        pathOpener: 'Path',
                        externalAsset: 'Asset',
                        accounting: 'ACC',
                    },
                },
                account: {
                    title: 'Account',
                    profile: 'Profile',
                    name: 'Name',
                    email: 'Email',
                    changePassword: 'Change password',
                    currentPassword: 'Current password',
                    newPassword: 'New password',
                    confirmPassword: 'Confirm new password',
                    updatePassword: 'Update password',
                    changePasswordSuccess: 'Password updated successfully',
                    changePasswordError: 'Failed to update password',
                    passwordMismatch: 'Passwords do not match',
                    togglePasswordVisibility: 'Toggle password visibility',
                    generatePassword: 'Generate password',
                    generatePasswordSuccess: 'Generated password copied to clipboard',
                    generatePasswordError: 'Could not copy password to clipboard',
                    approveDevice: 'Approve another device',
                    approveDeviceHelper:
                        'Sign in a TV or another computer that shows a short device code (no password typing on that device).',
                    approveDeviceAction: 'Enter device code',
                    sessions: 'Active sessions',
                    sessionsHelper:
                        'Each browser login and API token is a separate Sanctum token. Device details are captured when the token is created. Revoking a token signs out that device or disables that API key.',
                    sessionCurrent: 'This device',
                    sessionKindBrowser: 'Browser',
                    sessionKindApi: 'API token',
                    sessionDevice: 'Device',
                    sessionIp: 'IP',
                    sessionCountry: 'Country',
                    sessionUnknown: 'Unknown',
                    sessionLocal: 'Local network',
                    sessionUserAgent: 'User agent',
                    sessionCreated: 'Created',
                    sessionLastUsed: 'Last used',
                    sessionExpires: 'Expires',
                    sessionNeverUsed: 'Never used',
                    sessionNoExpiry: 'No expiry',
                    sessionRevoke: 'Revoke',
                    revokeOtherSessions: 'Revoke all other browser sessions',
                    revokeOtherSessionsSuccess: '{{count}} other browser session(s) revoked',
                    revokeOtherSessionsError: 'Failed to revoke other browser sessions',
                    revokeOtherSessionsNone: 'No other browser sessions to revoke',
                    sessionRevokeCurrent: 'Sign out',
                    sessionRevokeSuccess: 'Session revoked',
                    sessionRevokeError: 'Failed to revoke session',
                    sessionRevokeCurrentSuccess: 'Signed out',
                    noSessions: 'No active sessions',
                },
                apiTokens: {
                    title: 'API Tokens',
                    create: 'Create Token',
                    createButton: 'Create New Token',
                    createTitle: 'Create New API Token',
                    name: 'Token Name',
                    token: 'Token',
                    lastUsed: 'Last Used',
                    expiresAt: 'Expires At',
                    actions: 'Actions',
                    createSuccess: 'Token created successfully',
                    createError: 'Failed to create token',
                    deleteSuccess: 'Token deleted successfully',
                    deleteError: 'Failed to delete token',
                    copySuccess: 'Token copied to clipboard',
                    validation: {
                        name: {
                            required: 'Token name is required'
                        },
                        expiration: {
                            required: 'Expiration date is required'
                        }
                    },
                    noTokens: 'No API Tokens Yet',
                    noTokensDescription: 'Get started by creating your first API token. This will allow you to authenticate your API requests.',
                    form: {
                        name: 'Token Name',
                        expiration: 'Expiration Date',
                        expirationPlaceholder: 'Select expiration date'
                    },
                    neverUsed: 'Never Used',
                    tokenWarning: {
                        title: 'Important: Save Your Token',
                        description: 'This token will only be shown once. Please save it in a secure location. You won\'t be able to see it again.'
                    }
                },
                gallery: {
                    title: 'Gallery',
                    sliderTitle: 'Gallery Slider',
                    addImage: 'Add Image',
                    uploadTitle: 'Upload Image',
                    selectImage: 'Select Image',
                    selectedFiles: 'Selected Files',
                    upload: 'Upload',
                    uploadSuccess: 'Image uploaded successfully',
                    uploadError: 'Failed to upload image',
                    deleteTitle: 'Delete Image',
                    deleteConfirmation: 'Are you sure you want to delete this image?',
                    deleteSuccess: 'Image deleted successfully',
                    deleteError: 'Failed to delete image',
                    noImages: 'No images yet',
                    loadMore: 'Load More',
                    filterAll: 'All',
                    filterPhotos: 'Photos',
                    filterVideos: 'Videos',
                    sortNewest: 'Newest',
                    sortOldest: 'Oldest',
                    viewGrid: 'Grid view',
                    viewSlider: 'Slider view',
                    unavailable: 'Unavailable',
                    albumsTitle: 'Albums',
                    allPhotos: 'All photos',
                    createAlbum: 'New album',
                    createAlbumTitle: 'Create album',
                    albumName: 'Album name',
                    albumPhotoCount: '{{count}} photos',
                    uploadAlbum: 'Album',
                    noAlbum: 'No album',
                    deleteAlbumConfirmation: 'Delete this album? Photos will stay in All photos.',
                    addExistingPhotos: 'Add existing photos',
                    addSelectedPhotos: 'Add selected ({{count}})',
                    noPhotosToAdd: 'All library photos are already in this album.',
                    removeFromAlbumTitle: 'Remove from album',
                    removeFromAlbumConfirmation: 'Remove this photo from the album? The file stays in All photos.',
                    removeFromAlbumSuccess: 'Photo removed from album',
                    removeFromAlbumError: 'Failed to remove photo from album',
                    inAlbums: 'In albums',
                    alsoInAlbums: 'Also in albums',
                    notInAnyAlbum: 'Not in any album',
                    notInAnyOtherAlbum: 'Not in any other album',
                    displayName: 'Display name',
                    originalName: 'Original file',
                    displayNameSaved: 'Display name saved',
                },
                common: {
                    retry: 'Try again',
                    loadFailed: 'Could not load data (HTTP {{status}}): {{message}}',
                    cancel: 'Cancel',
                    close: 'Close',
                    error: 'An error occurred',
                    loading: 'Loading...',
                    or: 'OR',
                    yes: 'Yes',
                    no: 'No',
                    create: 'Create',
                    upload: 'Upload',
                    edit: 'Edit',
                    back: 'Back',
                    errorFetchingData: 'Error fetching data',
                    errorSavingData: 'Error saving data',
                    refreshData: 'Refresh Data',
                    save: 'Save',
                    clear: 'Clear',
                    find: 'Find',
                    next: 'Next',
                    changePositions: 'Change Positions',
                    delete: 'Delete',
                },
                validation: {
                    required: 'This field is required',
                    email: {
                        required: 'Email is required',
                        invalid: 'Invalid email format'
                    },
                    password: {
                        required: 'Password is required',
                        minLength: 'Password must be at least 8 characters long'
                    },
                    numberWithLength: 'Must be a number with up to {{length}} digits',
                    decimalWithLength: 'Must be a decimal with exactly {{length}} digits after the dot and up to 10 total digits (e.g., {{example}})',
                    stringWithLength: 'Must not be greater than {{length}} characters {{example}}'
                },
                tasks: {
                    title: 'Tasks',
                    createNew: 'Create New Task',
                    editTask: 'Edit Task',
                    createTask: 'Create Task',
                    name: 'Name',
                    description: 'Description',
                    priority: 'Priority',
                    priorityLow: 'Low',
                    priorityMedium: 'Medium',
                    priorityHigh: 'High',
                    status: 'Status',
                    active: 'Done',
                    inactive: 'Todo',
                    createdAt: 'Created At',
                    updatedAt: 'Updated At',
                    actions: 'Actions',
                    save: 'Save',
                    create: 'Create',
                    deleteTitle: 'Delete Task',
                    deleteConfirmation: 'Are you sure you want to delete this task?',
                    createSuccess: 'Task created successfully',
                    createError: 'Failed to create task',
                    updateSuccess: 'Task updated successfully',
                    updateError: 'Failed to update task',
                    deleteSuccess: 'Task deleted successfully',
                    deleteError: 'Failed to delete task',
                    noTasks: 'No tasks yet',
                    note: 'Note',
                    done: 'Done',
                    markAsDone: 'Mark as done',
                    markAsTodo: 'Mark as todo',
                    markAllAsTodo: 'Mark all as todo',
                    markAllAsTodoTooltip: 'Set every task on this note to todo (unchecked).',
                    markAllAsTodoSuccess: 'Marked {{count}} task(s) as todo.',
                    markAllAsTodoNone: 'No tasks were marked as done; nothing to change.',
                    markAllAsTodoError: 'Failed to mark all tasks as todo.',
                    dueDate: 'Due date',
                    completedAt: 'Completed At'
                },
                notes: {
                    title: 'Notes',
                    createNew: 'Create New Note',
                    editNote: 'Edit Note',
                    edit: 'Edit note',
                    createNote: 'Create Note',
                    name: 'Name',
                    status: 'Status',
                    statusPlaceholder: 'Planning, Active, On hold…',
                    description: 'Description',
                    relatedNotes: 'Related Notes',
                    createdAt: 'Created At',
                    actions: 'Actions',
                    save: 'Save',
                    create: 'Create',
                    update: 'Update',
                    deleteTitle: 'Delete Note',
                    deleteConfirmation: 'Are you sure you want to delete {{ name }} ?',
                    createSuccess: 'Note created successfully',
                    createError: 'Failed to create note',
                    updateSuccess: 'Note updated successfully',
                    updateError: 'Failed to update note',
                    deleteSuccess: 'Note deleted successfully',
                    deleteError: 'Failed to delete note',
                    noNotes: 'No notes yet',
                    back: 'Back',
                    search: 'Search notes',
                    searchByTitle: 'Search by title',
                    searchByDescription: 'Search by description',
                    export: 'Export',
                    exportTxt: 'Export as TXT',
                    exportHtml: 'Export as HTML',
                    exportPdf: 'Export as PDF',
                    sendEmail: 'Send by Email',
                    email: 'Email address',
                    subject: 'Subject',
                    send: 'Send',
                    sendEmailError: 'Failed to send email',
                    note: 'Note',
                    view: 'View Note',
                    additional: 'Additional',
                    notifyAboutChanges: 'Notify about changes',
                    reminderAt: 'Reminder at',
                    lastUpdated: 'Note was updated at {{ time }}',
                    celebrateOnSave: 'Celebrate on save (confetti & sound)'
                    ,
                    find: 'Find in note',
                    findPlaceholder: 'Enter search term',
                    findNoResults: 'No results',
                    findPrompt: 'Find:',
                    findNotSupported: 'Find not supported in this browser'
                },
                accounting: {
                    title: 'Accountings',
                    createNew: 'Create New',
                    createTitle: 'Create Accounting Record',
                    editTitle: 'Edit Accounting Record',
                    deleteTitle: 'Delete Record',
                    deleteConfirmation: 'Are you sure you want to delete this record?',
                    date: 'Date',
                    author: 'Recorded by',
                    referenceYear: 'Reference year',
                    referenceYearShort: 'Ref. year',
                    referenceYearHint: 'Tax or filing year this item relates to (may differ from document date).',
                    amountShort: 'Suma',
                    currencyShort: 'Mena',
                    doc: 'Doc',
                    description: 'Description',
                    accountDebit: 'Account Debit',
                    accountDebitDescription: 'Debit Description',
                    accountCredit: 'Account Credit',
                    accountCreditDescription: 'Credit Description',
                    amountCurrency: 'Amount Currency',
                    exchangeCurrency: 'Exchange Currency',
                    exchangeRate: 'Exchange Rate',
                    amount: 'Amount',
                    created_at: 'Created At',
                    updated_at: 'Updated At',
                    save: 'Save',
                    create: 'Create',
                    actions: 'Actions',
                    noRecords: 'No accounting records yet',
                    createSuccess: 'Record created successfully',
                    createError: 'Failed to create record',
                    updateSuccess: 'Record updated successfully',
                    updateError: 'Failed to update record',
                    deleteSuccess: 'Record deleted successfully',
                    deleteError: 'Failed to delete record',
                    generatePdf: 'Generate pdf',
                    searchAllColumns: 'Search across all columns...',
                    searchMode: 'Search mode',
                    kindBooking: 'Booking',
                    kindDocument: 'Document',
                    createBooking: 'New booking',
                    createDocument: 'Upload document',
                    quickFilters: 'Quick filters',
                    filterLastYear: 'Last year ({{year}})',
                    filterDocumentsOnly: 'Documents only',
                    downloadAttachments: 'Download attachments (ZIP)',
                    downloadAttachmentsPreparing: 'Preparing ZIP… this can take a minute for many files.',
                    downloadAttachmentsEmpty: 'No attachments match the current filters.',
                    downloadAttachmentsError: 'Could not download attachments.',
                    downloadAttachmentsNetworkError:
                        'Connection dropped while building the ZIP (often a timeout). Wait a moment and try again.',
                },
                externalAssets: {
                    title: 'External Assets',
                    createNew: 'Create New Asset',
                    createTitle: 'Create External Asset',
                    editTitle: 'Edit External Asset',
                    deleteTitle: 'Delete External Asset',
                    deleteConfirmation: 'Are you sure you want to delete this external asset?',
                    columns: {
                        title: 'Title',
                        info: 'Info / recap / hodnotenie',
                        filePath: 'File Path',
                        publisher: 'Publisher',
                        source: 'Source',
                        type: 'Type',
                        status: 'Status',
                        projects: 'Projects',
                        tags: 'Tags',
                        url: 'URL',
                        notes: 'Notes',
                        updatedAt: 'Last updated'
                    },
                    infoHint: 'Short recap + rating (Jodit). Template is prefilled for new assets.',
                    sortByEdit: 'By last edit',
                    sortByTitle: 'A–Z (title)',
                    orderBy: 'Order by',
                    openLinkHint: 'Open link in new tab',
                    copyAllPaths: 'Copy all paths',
                    frequentSearchTags: 'Frequently searched tags',
                    dropLinkHint: 'Drop a link in the box, right-click to paste from clipboard (creates asset immediately), or focus the box and press Ctrl+V.',
                    dropLinkZone: 'Drop link here · right-click = paste · Ctrl+V',
                    pasteLinkWorking: 'Creating asset…',
                    dropLinkZoneActive: 'Release to create external asset',
                    pasteLinkFromClipboard: 'Paste link from clipboard',
                    clipboardEmpty: 'Clipboard has no link. Copy a hyperlink in the browser first (right-click the link → Copy link).',
                    clipboardReadError: 'Could not read clipboard. Allow clipboard access or use Ctrl+V in this box.',
                    dropLinkOverlay: 'Drop link to add external asset',
                    dropLinkInvalid: 'Could not read a URL from the dropped item.',
                    messages: {
                        createSuccess: 'External asset created successfully',
                        createError: 'Failed to create external asset',
                        updateSuccess: 'External asset updated successfully',
                        updateError: 'Failed to update external asset',
                        deleteSuccess: 'External asset deleted successfully',
                        deleteError: 'Failed to delete external asset'
                    }
                },
                tips: {
                    title: 'Tips',
                    saveShortcut: 'Press {{ btn }} to save quickly',
                    toggleShortcut: '{{ btn }} to toggle text case (select text first)',
                    insertTimestampShortcut: '{{ btn }} to insert timestamp',
                    shortcutsTooltip: 'Keyboard shortcuts',
                    filterPlaceholder: 'Filter shortcuts',
                    focusSearch: 'Press / to focus global note search'
                    ,
                    makeCopyable: 'Make selected text copyable (wrap with [[brackets]])'
                }
                ,
                shortcuts: {
                    editor: 'Note Editor shortcuts',
                    global: 'Global shortcuts',
                    newNote: 'New note'
                },
                workflow: {
                    statusLabel: 'Workflow',
                    status: {
                        todo: 'To do',
                        in_progress: 'In progress',
                        blocked: 'Blocked',
                        done: 'Done',
                    },
                    assignee: 'Assignee',
                    unassigned: 'Unassigned',
                    createdBy: 'Created by',
                },
                comments: {
                    title: 'Comments',
                    empty: 'No comments yet.',
                    placeholder: 'Write a comment… (Ctrl+Enter to send)',
                    add: 'Send',
                    save: 'Save',
                    edit: 'Edit',
                    edited: 'edited',
                    delete: 'Delete',
                    deleteConfirm: 'Delete this comment?',
                    saveError: 'Failed to save the comment',
                    deleteError: 'Failed to delete the comment',
                },
                tags: {
                    title: 'Tags',
                    placeholder: 'Add tag…',
                    filter: 'Filter by tag',
                    all: 'All',
                    saveError: 'Failed to save tags',
                },
                bots: {
                    title: 'Bots',
                    subtitle: 'Each bot has its own identity and tokens. Bots work in your workspace and everything they do is recorded under their name.',
                    create: 'Add bot',
                    name: 'Name',
                    role: 'Role',
                    color: 'Color',
                    active: 'Active',
                    inactive: 'Disabled',
                    lastActive: 'Last active',
                    never: 'never',
                    tokens: 'Tokens',
                    newToken: 'New token',
                    tokenName: 'Token name',
                    abilities: 'Abilities',
                    abilitiesHint: 'Comma separated, e.g. tasks:*, projects:read, music:*  (use * for full access)',
                    createToken: 'Create token',
                    tokenCreated: 'Token created – copy it now, it will not be shown again:',
                    copy: 'Copy',
                    copied: 'Copied',
                    revoke: 'Revoke',
                    revokeConfirm: 'Revoke this token? The bot will lose access immediately.',
                    deactivate: 'Disable',
                    deactivateConfirm: 'Disable this bot and revoke all its tokens? Its history stays.',
                    reactivate: 'Enable',
                    noBots: 'No bots yet.',
                    noTokens: 'No tokens.',
                    saveError: 'Failed to save the bot',
                    created: 'Bot created',
                },
                music: {
                    title: 'Music',
                    upload: 'Upload tracks',
                    uploading: 'Uploading {{done}}/{{total}}…',
                    uploadError: 'Upload failed: {{name}}',
                    library: 'Library',
                    allTracks: 'All tracks',
                    playlists: 'Playlists',
                    newPlaylist: 'New playlist',
                    playlistName: 'Playlist name',
                    addToPlaylist: 'Add to playlist',
                    removeFromPlaylist: 'Remove from playlist',
                    deletePlaylist: 'Delete playlist',
                    deleteTrack: 'Delete track',
                    deleteTrackConfirm: 'Delete this track and its file?',
                    search: 'Search title, artist, album…',
                    trackTitle: 'Title',
                    artist: 'Artist',
                    album: 'Album',
                    duration: 'Duration',
                    empty: 'No tracks yet. Upload mp3, ogg, wav, flac or m4a files.',
                    nothingPlaying: 'Nothing playing',
                    play: 'Play',
                    pause: 'Pause',
                    next: 'Next',
                    previous: 'Previous',
                    volume: 'Volume',
                    shuffle: 'Shuffle',
                    repeat: 'Repeat',
                    remote: 'Remote control: other devices (e.g. the UE player) can control this player via the API.',
                    remoteBy: 'Remote command from {{name}}',
                    albums: 'Albums',
                    noAlbum: 'No album',
                    all: 'All',
                    download: 'Download',
                    downloadAlbum: 'Download album',
                    downloadError: 'Download failed',
                    downloadStarted: 'Downloading {{count}} tracks ({{size}})…',
                    downloadConfirm: 'Download {{count}} tracks ({{size}})? This is a large download.',
                    downloadEmpty: 'No tracks to download.',
                    source: 'Source',
                    license: 'License',
                    sourceUrl: 'Source link',
                    albumPath: 'Album path',
                    albumPathHint: 'e.g. Game/Antimatter/Airlock',
                    editTrack: 'Edit track',
                    save: 'Save',
                    cancel: 'Cancel',
                    saveError: 'Saving failed',
                    selectedTracks: '{{count}} tracks · {{size}}',
                    view: { tree: 'Tree', cards: 'Albums' },
                    cards: {
                        empty: 'No albums yet.',
                        tracks_one: '{{count}} track',
                        tracks_other: '{{count}} tracks',
                        subAlbums_one: '{{count}} sub-album',
                        subAlbums_other: '{{count}} sub-albums',
                        unrated: 'not rated',
                        ratedOf: '{{rated}} of {{count}} tracks rated',
                    },
                    rating: 'Rating',
                    downloadBest: 'Download Best',
                    downloadBestHint: 'Download all tracks of this album rated {{min}}★ or more',
                    advanced: {
                        title: 'Advanced search',
                        text: 'Text',
                        exact: 'Exact search',
                        exactHint: 'Exact = whole word or phrase, ignores case ("tron" finds "Tron Legacy" but not "strong"). Off = any text containing it.',
                        searchIn: 'Search in:',
                        field: { title: 'Title', artist: 'Artist', album: 'Album', filename: 'File name' },
                        sourceNot: 'all except this',
                        ratingAny: 'Any rating',
                        ratingMin: 'At least …',
                        ratingExact: 'Exactly …',
                        unrated: 'unrated',
                        minMinutes: 'Min. length (min)',
                        maxMinutes: 'Max. length (min)',
                        format: 'Format:',
                        sortBy: 'Sort by',
                        sort: { title: 'Title', artist: 'Artist', album: 'Album', rating: 'Rating', duration: 'Duration', size: 'Size' },
                        asc: 'Ascending',
                        desc: 'Descending',
                        search: 'Search',
                        reset: 'Reset',
                        results: '{{count}} tracks found',
                    },
                },
                language: {
                    label: 'Language',
                    en: 'English',
                    sk: 'Slovenčina',
                },
                fileManager: {
                    title: 'File Manager',
                },
                projects: {
                    title: 'Projects',
                },
                pathOpener: {
                    title: 'Path Opener',
                    searchPlaceholder: 'Search by path, URL, name, description… (also id / mode)',
                    searchMode: 'Search mode',
                    searchModeContains: 'Exact (contains)',
                    searchModeFuzzy: 'Fuzzy',
                    searchModeStartsWith: 'Starts with',
                    quickFilters: 'Quick filters',
                    sortByPriority: 'Priority',
                    sortByUpdated: 'Updated',
                    description: 'Paths synced with the Unreal Path Opener widget (same catalog as the editor).',
                    create: 'Add entry',
                    createTitle: 'New path entry',
                    editTitle: 'Edit path entry',
                    deleteTitle: 'Delete path entry',
                    deleteConfirmation: 'Remove this entry from the catalog?',
                    positionHint: 'Optional tie-breaker when two rows share the same priority (lower number sorts first). Leave empty for 0 — new rows are no longer auto-numbered.',
                    priorityHint: 'Same scale as Tasks (lower number = higher priority). Shown above Order in the list.',
                    remarkHint: 'Optional — remind yourself what this path/URL is for (not used when opening).',
                    pathClickOpen: 'Click to open link',
                    pathClickCopy: 'Click to copy path',
                    columns: {
                        id: 'ID',
                        value: 'Path or URL',
                        name: 'Name',
                        description: 'Description',
                        mode: 'Mode',
                        priority: 'Priority',
                        position: 'Order',
                        updatedAt: 'Last updated',
                    },
                    messages: {
                        createSuccess: 'Path entry created',
                        updateSuccess: 'Path entry updated',
                        deleteSuccess: 'Path entry deleted',
                        deleteError: 'Failed to delete path entry',
                    },
                },
            }
        },
        sk: {
            translation: {
                common: {
                    retry: 'Skúsiť znova',
                    loadFailed: 'Údaje sa nepodarilo načítať (HTTP {{status}}): {{message}}',
                },
                workflow: {
                    statusLabel: 'Stav',
                    status: {
                        todo: 'Na spracovanie',
                        in_progress: 'Rozpracované',
                        blocked: 'Blokované',
                        done: 'Hotové',
                    },
                    assignee: 'Riešiteľ',
                    unassigned: 'Nepridelené',
                    createdBy: 'Vytvoril',
                },
                comments: {
                    title: 'Komentáre',
                    empty: 'Zatiaľ žiadne komentáre.',
                    placeholder: 'Napíš komentár… (Ctrl+Enter odošle)',
                    add: 'Odoslať',
                    save: 'Uložiť',
                    edit: 'Upraviť',
                    edited: 'upravené',
                    delete: 'Zmazať',
                    deleteConfirm: 'Zmazať tento komentár?',
                    saveError: 'Komentár sa nepodarilo uložiť',
                    deleteError: 'Komentár sa nepodarilo zmazať',
                },
                tags: {
                    title: 'Tagy',
                    placeholder: 'Pridať tag…',
                    filter: 'Filtrovať podľa tagu',
                    all: 'Všetky',
                    saveError: 'Tagy sa nepodarilo uložiť',
                },
                bots: {
                    title: 'Boti',
                    subtitle: 'Každý bot má vlastnú identitu a tokeny. Boti pracujú v tvojom priestore a všetko, čo spravia, sa zapisuje pod ich menom.',
                    create: 'Pridať bota',
                    name: 'Meno',
                    role: 'Rola',
                    color: 'Farba',
                    active: 'Aktívny',
                    inactive: 'Vypnutý',
                    lastActive: 'Naposledy aktívny',
                    never: 'nikdy',
                    tokens: 'Tokeny',
                    newToken: 'Nový token',
                    tokenName: 'Názov tokenu',
                    abilities: 'Oprávnenia',
                    abilitiesHint: 'Oddelené čiarkou, napr. tasks:*, projects:read, music:*  (* = plný prístup)',
                    createToken: 'Vytvoriť token',
                    tokenCreated: 'Token je vytvorený – skopíruj si ho teraz, znova sa už nezobrazí:',
                    copy: 'Kopírovať',
                    copied: 'Skopírované',
                    revoke: 'Zrušiť',
                    revokeConfirm: 'Zrušiť tento token? Bot okamžite stratí prístup.',
                    deactivate: 'Vypnúť',
                    deactivateConfirm: 'Vypnúť tohto bota a zrušiť všetky jeho tokeny? História ostane.',
                    reactivate: 'Zapnúť',
                    noBots: 'Zatiaľ žiadni boti.',
                    noTokens: 'Žiadne tokeny.',
                    saveError: 'Bota sa nepodarilo uložiť',
                    created: 'Bot vytvorený',
                },
                music: {
                    title: 'Hudba',
                    upload: 'Nahrať skladby',
                    uploading: 'Nahrávam {{done}}/{{total}}…',
                    uploadError: 'Nahratie zlyhalo: {{name}}',
                    library: 'Knižnica',
                    allTracks: 'Všetky skladby',
                    playlists: 'Playlisty',
                    newPlaylist: 'Nový playlist',
                    playlistName: 'Názov playlistu',
                    addToPlaylist: 'Pridať do playlistu',
                    removeFromPlaylist: 'Odobrať z playlistu',
                    deletePlaylist: 'Zmazať playlist',
                    deleteTrack: 'Zmazať skladbu',
                    deleteTrackConfirm: 'Zmazať túto skladbu aj jej súbor?',
                    search: 'Hľadať názov, interpreta, album…',
                    trackTitle: 'Názov',
                    artist: 'Interpret',
                    album: 'Album',
                    duration: 'Dĺžka',
                    empty: 'Zatiaľ žiadne skladby. Nahraj mp3, ogg, wav, flac alebo m4a.',
                    nothingPlaying: 'Nič nehrá',
                    play: 'Prehrať',
                    pause: 'Pauza',
                    next: 'Ďalšia',
                    previous: 'Predchádzajúca',
                    volume: 'Hlasitosť',
                    shuffle: 'Náhodne',
                    repeat: 'Opakovať',
                    remote: 'Diaľkové ovládanie: iné zariadenia (napr. UE prehrávač) môžu tento prehrávač ovládať cez API.',
                    remoteBy: 'Príkaz na diaľku od {{name}}',
                    albums: 'Albumy',
                    noAlbum: 'Bez albumu',
                    all: 'Všetko',
                    download: 'Stiahnuť',
                    downloadAlbum: 'Stiahnuť album',
                    downloadError: 'Sťahovanie zlyhalo',
                    downloadStarted: 'Sťahujem {{count}} skladieb ({{size}})…',
                    downloadConfirm: 'Stiahnuť {{count}} skladieb ({{size}})? Je to veľké sťahovanie.',
                    downloadEmpty: 'Žiadne skladby na stiahnutie.',
                    source: 'Zdroj',
                    license: 'Licencia',
                    sourceUrl: 'Odkaz na zdroj',
                    albumPath: 'Cesta albumu',
                    albumPathHint: 'napr. Game/Antimatter/Airlock',
                    editTrack: 'Upraviť skladbu',
                    save: 'Uložiť',
                    cancel: 'Zrušiť',
                    saveError: 'Uloženie zlyhalo',
                    selectedTracks: '{{count}} skladieb · {{size}}',
                    view: { tree: 'Strom', cards: 'Albumy' },
                    cards: {
                        empty: 'Zatiaľ žiadne albumy.',
                        tracks_one: '{{count}} skladba',
                        tracks_few: '{{count}} skladby',
                        tracks_many: '{{count}} skladby',
                        tracks_other: '{{count}} skladieb',
                        subAlbums_one: '{{count}} podalbum',
                        subAlbums_few: '{{count}} podalbumy',
                        subAlbums_many: '{{count}} podalbumu',
                        subAlbums_other: '{{count}} podalbumov',
                        unrated: 'bez hodnotenia',
                        ratedOf: '{{rated}} z {{count}} skladieb hodnotených',
                    },
                    rating: 'Hodnotenie',
                    downloadBest: 'Stiahnuť Best',
                    downloadBestHint: 'Stiahnuť všetky skladby tohto albumu s hodnotením {{min}}★ a viac',
                    advanced: {
                        title: 'Rozšírené vyhľadávanie',
                        text: 'Text',
                        exact: 'Presné hľadanie',
                        exactHint: 'Presné = celé slovo alebo fráza bez ohľadu na veľkosť písmen („tron“ nájde „Tron Legacy“, ale nie „strong“). Vypnuté = čokoľvek, čo text obsahuje.',
                        searchIn: 'Hľadať v:',
                        field: { title: 'Názov', artist: 'Interpret', album: 'Album', filename: 'Názov súboru' },
                        sourceNot: 'všetko okrem tohto',
                        ratingAny: 'Akékoľvek hodnotenie',
                        ratingMin: 'Aspoň …',
                        ratingExact: 'Presne …',
                        unrated: 'bez hodnotenia',
                        minMinutes: 'Min. dĺžka (min)',
                        maxMinutes: 'Max. dĺžka (min)',
                        format: 'Formát:',
                        sortBy: 'Zoradiť podľa',
                        sort: { title: 'Názov', artist: 'Interpret', album: 'Album', rating: 'Hodnotenie', duration: 'Dĺžka', size: 'Veľkosť' },
                        asc: 'Vzostupne',
                        desc: 'Zostupne',
                        search: 'Hľadať',
                        reset: 'Vynulovať',
                        results: 'Nájdených skladieb: {{count}}',
                    },
                },
                language: {
                    label: 'Jazyk',
                    en: 'English',
                    sk: 'Slovenčina',
                },
                externalAssets: {
                    openLinkHint: 'Otvoriť odkaz v novom tabe',
                    copyAllPaths: 'Kopírovať všetky cesty',
                    frequentSearchTags: 'Často hľadané tagy',
                    dropLinkHint: 'Pustite odkaz do rámčeka, alebo skopírujte odkaz v prehliadači a tu pravý klik (vytvorí asset hneď) alebo Ctrl+V.',
                    dropLinkZone: 'Sem pustite odkaz · pravý klik = vložiť · Ctrl+V',
                    pasteLinkWorking: 'Vytváram asset…',
                    dropLinkZoneActive: 'Pustite — vytvorí sa external asset',
                    pasteLinkFromClipboard: 'Vložiť odkaz zo schránky',
                    clipboardEmpty: 'Schránka neobsahuje odkaz. Najprv skopírujte hyperodkaz v prehliadači.',
                    clipboardReadError: 'Schránku sa nepodarilo prečítať. Povoľte prístup alebo použite Ctrl+V v tomto rámčeku.',
                    dropLinkOverlay: 'Pustite odkaz — pridá sa external asset',
                    dropLinkInvalid: 'Nepodarilo sa načítať URL z pretiahnutého odkazu.',
                },
            },
        },
    }
});

export default i18n;
